import { supabase } from '../config/supabase';
import i18n from '../locales';
import {
  OptionListType,
  OptionItem,
  OptionListEntry,
  CustomOptionValue,
} from '../types';

export const optionListsService = {
  /**
   * Get active options for a list type
   */
  async getOptions(
    listType: OptionListType,
    scope?: { type: string; value: string }
  ): Promise<OptionItem[]> {
    let query = supabase
      .from('option_lists')
      .select('value, label_en, label_nl')
      .eq('list_type', listType)
      .eq('is_active', true);

    if (scope) {
      query = query.or(
        `scope_type.is.null,and(scope_type.eq.${scope.type},scope_value.eq.${scope.value})`
      );
    } else {
      query = query.is('scope_type', null);
    }

    const { data, error } = await query.order('sort_order');

    if (error) throw error;

    return (data || []).map((opt) => ({
      value: opt.value,
      label: i18n.language === 'nl' ? opt.label_nl : opt.label_en,
    }));
  },

  /**
   * Check if a value is an official option
   */
  async isOfficialOption(listType: OptionListType, value: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('option_lists')
      .select('id')
      .eq('list_type', listType)
      .eq('value', value.toLowerCase())
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return data !== null;
  },

  /**
   * Track usage of a custom value
   */
  async trackCustomValue(listType: OptionListType, value: string): Promise<void> {
    const normalizedValue = value.toLowerCase().trim();

    // Check if already promoted
    const { data: existing } = await supabase
      .from('custom_option_values')
      .select('id, promoted_to_option_id')
      .eq('list_type', listType)
      .eq('normalized_value', normalizedValue)
      .maybeSingle();

    if (existing?.promoted_to_option_id) {
      // Already promoted, no need to track
      return;
    }

    if (existing) {
      // Increment usage count
      await supabase
        .from('custom_option_values')
        .update({
          usage_count: existing.id ? undefined : 1, // Will use SQL increment
          last_used_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      // Use RPC for atomic increment
      await supabase.rpc('increment_custom_value_count', {
        custom_value_id: existing.id,
      });
    } else {
      // Insert new custom value
      await supabase.from('custom_option_values').insert({
        list_type: listType,
        value: value.trim(),
        normalized_value: normalizedValue,
        usage_count: 1,
      });
    }
  },

  /**
   * Get all options for admin management (including inactive)
   */
  async getAllOptionsAdmin(listType: OptionListType): Promise<OptionListEntry[]> {
    const { data, error } = await supabase
      .from('option_lists')
      .select('*')
      .eq('list_type', listType)
      .order('sort_order');

    if (error) throw error;
    return data || [];
  },

  /**
   * Get pending custom values for admin review
   */
  async getPendingCustomValues(
    listType: OptionListType
  ): Promise<CustomOptionValue[]> {
    const { data, error } = await supabase
      .from('custom_option_values')
      .select('*')
      .eq('list_type', listType)
      .is('promoted_to_option_id', null)
      .order('usage_count', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get count of pending custom values (for admin badge)
   */
  async getPendingCustomValuesCount(): Promise<number> {
    const { count, error } = await supabase
      .from('custom_option_values')
      .select('*', { count: 'exact', head: true })
      .is('promoted_to_option_id', null)
      .gte('usage_count', 5);

    if (error) throw error;
    return count || 0;
  },

  /**
   * Create a new option (admin only)
   */
  async createOption(option: Omit<OptionListEntry, 'id' | 'created_at' | 'updated_at'>): Promise<OptionListEntry> {
    const { data, error } = await supabase
      .from('option_lists')
      .insert(option)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update an option (admin only)
   */
  async updateOption(
    id: string,
    updates: Partial<Omit<OptionListEntry, 'id' | 'created_at'>>
  ): Promise<OptionListEntry> {
    const { data, error } = await supabase
      .from('option_lists')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete an option (admin only) - soft delete by setting is_active = false
   */
  async deleteOption(id: string): Promise<void> {
    const { error } = await supabase
      .from('option_lists')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Find similar custom values using fuzzy matching
   */
  async findSimilarCustomValues(
    listType: OptionListType,
    normalizedValue: string
  ): Promise<CustomOptionValue[]> {
    // Use PostgreSQL levenshtein function
    const { data, error } = await supabase.rpc('find_similar_custom_values', {
      p_list_type: listType,
      p_normalized_value: normalizedValue,
      p_max_distance: 2,
    });

    if (error) throw error;
    return data || [];
  },

  /**
   * Promote custom value(s) to official option
   */
  async promoteCustomValues(
    customValueIds: string[],
    newOption: Omit<OptionListEntry, 'id' | 'created_at' | 'updated_at'>
  ): Promise<{ option: OptionListEntry; updatedItemsCount: number }> {
    // Get the custom values to merge
    const { data: customValues, error: fetchError } = await supabase
      .from('custom_option_values')
      .select('*')
      .in('id', customValueIds);

    if (fetchError) throw fetchError;

    // Create the new official option
    const { data: option, error: insertError } = await supabase
      .from('option_lists')
      .insert(newOption)
      .select()
      .single();

    if (insertError) throw insertError;

    // Mark custom values as promoted
    const { error: updateError } = await supabase
      .from('custom_option_values')
      .update({ promoted_to_option_id: option.id })
      .in('id', customValueIds);

    if (updateError) throw updateError;

    // Update all clothing items with matching values
    const valuesToNormalize = customValues.map((cv) => cv.value);
    const { data: updatedItems, error: itemsError } = await supabase
      .from('clothing_items')
      .update({ [newOption.list_type]: option.value })
      .in(newOption.list_type, valuesToNormalize)
      .select('id');

    if (itemsError) throw itemsError;

    return { option, updatedItemsCount: updatedItems?.length || 0 };
  },
};
